import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
// Agreement PDFs generated by backend
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { getCountryName } from '../../../utils/countries';
import { natureLabel, oemOdmLabel, subcategoryLabel } from '../../../utils/classification';
import {
  formatSupplierCategory,
  formatSupplierType,
  formatBusinessVertical,
  formatAccountType,
  formatSupplierGroup
} from '../../../utils/formatters';
import IndianVendorQuestionnaire from './IndianVendorQuestionnaire';
import ForeignVendorQuestionnaire from './ForeignVendorQuestionnaire';
import AssignVendorCodeModal from '../../vendor-master-list/components/AssignVendorCodeModal';
import { API_BASE_URL } from '../../../config/api';
import { documentService, vendorService, getErrorMessage } from '../../../services/apiService';
import { useToast } from '../../../components/ui/Toast';

/**
 * Vendor.kyc_manual_reason, written at registration: "registry timeout at <iso>; bank slow at <iso>; udyam upstream at <iso>"
 * — which registry could not answer, how, and when. Read back here in words.
 */
const MANUAL_WHAT = { registry: 'GSTIN and company details', bank: 'Bank account', udyam: 'Udyam registration' };
const MANUAL_WHY = {
  slow: 'the check was taking too long',
  timeout: 'the check timed out',
  upstream: 'the verification service was down',
  network: 'the verification service could not be reached',
  auth: 'the verification service refused the request',
  forbidden: 'automatic checking is switched off',
  unavailable: 'automatic checking is switched off',
  bad_response: 'the verification service answered wrongly',
  unknown: 'the verification service did not answer',
};
const parseManualReason = (text) =>
  String(text || '')
    .split(';')
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const m = p.match(/^(registry|bank|udyam) (\S+) at (\S+)$/);
      return m ? { what: MANUAL_WHAT[m[1]], why: MANUAL_WHY[m[2]] || m[2], at: m[3] } : { what: p, why: '', at: null };
    });

/**
 * qualityDecision (only for approval_level QUALITY, the quality gate before Level 1):
 *   { canDecide: boolean, approverNames: string, onDecide: (status: 'APPROVED'|'REJECTED', reason?) => Promise }
 * The footer then shows "Reject" / "Quality approved" instead of the Level 1 actions.
 */
const ReviewPanel = ({ application, isAdmin = false, onClose, onApprove, onReject, onRequestChanges, onAssignVendorCode, onRefresh, qualityDecision }) => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('supplier');
  const isQualityStep = application?.approval_level === 'QUALITY';
  const [showQualityReject, setShowQualityReject] = useState(false);
  const [qualityReason, setQualityReason] = useState('');
  const [qualityBusy, setQualityBusy] = useState(false);
  const decideQuality = async (status) => {
    if (!qualityDecision?.onDecide) return;
    if (status === 'REJECTED' && !qualityReason.trim()) return;
    setQualityBusy(true);
    try {
      await qualityDecision.onDecide(status, status === 'REJECTED' ? qualityReason.trim() : undefined);
    } finally {
      setQualityBusy(false);
    }
  };
  const [remarks, setRemarks] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showRejectionOptions, setShowRejectionOptions] = useState(false);
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const [showPermanentRejectionConfirmation, setShowPermanentRejectionConfirmation] = useState(false);
  const [rejectionType, setRejectionType] = useState(null); // 'permanent' | 'rectification'
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);

  // Questionnaire form data
  const [questionnaireData, setQuestionnaireData] = useState({
    supplierTermOfPayment: '',
    supplierPaymentMethod: '',
    supplierDeliveryTerms: '',
    supplierModeOfDelivery: '',
    supplierGroup: '',
    supplierCategory: '',
    commodityCode: ''
  });
  const [questionnaireErrors, setQuestionnaireErrors] = useState({});
  const [loadingAgreement, setLoadingAgreement] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showRequestDocumentsModal, setShowRequestDocumentsModal] = useState(false);
  const [requestedDocTypes, setRequestedDocTypes] = useState([]);
  const [customDocs, setCustomDocs] = useState([]);
  const [customDocInput, setCustomDocInput] = useState('');
  const [isRequestingDocuments, setIsRequestingDocuments] = useState(false);
  const [regeneratingMsmeId, setRegeneratingMsmeId] = useState(null);
  const [showAssignVendorCodeModal, setShowAssignVendorCodeModal] = useState(false);

  // Auto-poll when agreements are being generated
  useEffect(() => {
    const status = application?.agreement_generation_status;
    if (status !== 'PENDING' && status !== 'GENERATING') return;
    if (!onRefresh) return;
    const interval = setInterval(onRefresh, 7000);
    return () => clearInterval(interval);
  }, [application?.agreement_generation_status, onRefresh]);

  // Pre-populate questionnaire for LEVEL_2 approvals
  useEffect(() => {
    if (application?.approval_level === 'LEVEL_2' && application) {
      // Check if vendor has questionnaire data populated
      const hasQuestionnaireData = 
        application.supplier_term_of_payment ||
        application.supplier_payment_method ||
        application.supplier_delivery_terms ||
        application.supplier_mode_of_delivery;

      if (hasQuestionnaireData) {
        setQuestionnaireData({
          supplierTermOfPayment: application.supplier_term_of_payment || '',
          supplierPaymentMethod: application.supplier_payment_method || '',
          supplierDeliveryTerms: application.supplier_delivery_terms || '',
          supplierModeOfDelivery: application.supplier_mode_of_delivery || '',
          supplierGroup: application.supplier_group || '',
          supplierCategory: application.supplier_category || '',
          commodityCode: application.commodity_code || ''
        });
        console.log('✅ Pre-populated questionnaire from resolved vendor data');
      }
    }
  }, [application]);

  // Human-friendly document type labels
  const formatDocumentType = (docType) => {
    if (!docType) return 'Document';
    const map = {
      gst_certificate: 'GST Certificate',
      pan_card: 'PAN Card',
      bank_statement: 'Bank Statement',
      msme_certificate: 'MSME Certificate',
      msme_declaration: 'MSME Declaration',
      company_registration: 'Company Registration',
      incorporation_certificate: 'Incorporation Certificate',
      business_license: 'Business License',
      insurance_certificate: 'Insurance Certificate',
      quality_certificate: 'Quality Certificate',
      tax_certificate: 'Tax Certificate',
      other: 'Other Document'
    };
    if (map[docType]) return map[docType];
    // Fallback: titleize with spaces
    return String(docType)
      .split('_')
      .map(w => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
      .join(' ');
  };

  const tabs = [
    { id: 'supplier', label: 'Supplier Info', icon: 'Building2' },
    { id: 'bank', label: 'Bank Details', icon: 'CreditCard' },
    { id: 'documents', label: 'Documents', icon: 'FileText' },
    { id: 'agreements', label: 'Agreements', icon: 'FileCheck' },
    { id: 'history', label: 'History', icon: 'Clock' }
  ];

  const rejectionReasons = [
    { value: 'incomplete_documents', label: 'Incomplete Documents' },
    { value: 'invalid_information', label: 'Invalid Information' },
    { value: 'compliance_issues', label: 'Compliance Issues' },
    { value: 'duplicate_vendor', label: 'Duplicate Vendor' },
    { value: 'other', label: 'Other (specify)' }
  ];

  // Check if vendor is Indian
  const isIndianVendor = application.country_origin === 'IN';

  const updateQuestionnaireData = (updates) => {
    setQuestionnaireData(prev => ({ ...prev, ...updates }));
    // Clear errors for updated fields
    const updatedFields = Object.keys(updates);
    setQuestionnaireErrors(prev => {
      const newErrors = { ...prev };
      updatedFields.forEach(field => delete newErrors[field]);
      return newErrors;
    });
  };

  const validateQuestionnaire = () => {
    const errors = {};
    if (!questionnaireData.supplierTermOfPayment) errors.supplierTermOfPayment = 'Required';
    if (!questionnaireData.supplierPaymentMethod) errors.supplierPaymentMethod = 'Required';
    if (!questionnaireData.supplierDeliveryTerms) errors.supplierDeliveryTerms = 'Required';
    if (!questionnaireData.supplierModeOfDelivery) errors.supplierModeOfDelivery = 'Required';
    if (!questionnaireData.supplierGroup) errors.supplierGroup = 'Required';
    if (!questionnaireData.supplierCategory) errors.supplierCategory = 'Required';
    if (!questionnaireData.commodityCode) errors.commodityCode = 'Required';
    return errors;
  };

  console.log(application);

  // Helper function to fetch digital signature and convert to base64 data URL
  const fetchDigitalSignature = async (vendorId) => {
    try {
      // Fetch all documents for the vendor
      const documentsResponse = await fetch(`${API_BASE_URL}/documents/vendor/${vendorId}`);
      if (!documentsResponse.ok) {
        console.error('Failed to fetch vendor documents');
        return null;
      }

      const documents = await documentsResponse.json();

      // Find the digital signature document
      const digitalSignatureDoc = documents.find(doc => doc.document_type === 'DIGITAL_SIGNATURE');

      if (!digitalSignatureDoc) {
        console.warn('Digital signature document not found for vendor');
        return null;
      }

      // Download the digital signature file using authenticated endpoint
      const downloadResponse = await documentService.downloadDocument(digitalSignatureDoc.id);
      if (!downloadResponse || !downloadResponse.data) {
        console.error('Failed to download digital signature');
        return null;
      }

      // Convert to blob then to base64 data URL
      const blob = new Blob([downloadResponse.data], { type: downloadResponse.headers['content-type'] || 'application/octet-stream' });
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error fetching digital signature:', error);
      return null;
    }
  };

  const getVendorData = async () => {
    // Fetch digital signature
    const digitalSignature = await fetchDigitalSignature(application.id);

    return {
      // Core identity
      companyName: application.company_name,
      contactPersonName: application.contact_person_name,
      designation: application.designation,
      // Submission date for document rendering
      submissionDate: application.created_at || application.createdAt,
      // Addresses
      registeredAddress: application.registered_address,
      registeredCity: application.registered_city,
      registeredState: application.registered_state,
      registeredCountry: application.registered_country,
      registeredPincode: application.registered_pincode,
      supplyAddress: application.supply_address,
      supplyCity: application.supply_city,
      supplyState: application.supply_state,
      supplyPincode: application.supply_pincode,
      supplyCountry: application.supply_country,
      // Origin and business
      countryOrigin: application.country_origin,
      businessVertical: application.business_vertical,
      // Include snake_case duplicate for any generator that still reads it
      business_vertical: application.business_vertical,
      // Digital signature as base64 data URL
      digitalSignature: digitalSignature,
    };
  };

  const AGREEMENT_TYPE_TO_API = {
    nda: 'NDA',
    sqa: 'SQA',
    four_m: 'FOUR_M',
    code_of_conduct: 'CODE_OF_CONDUCT',
    compliance_agreement: 'COMPLIANCE_AGREEMENT',
    self_declaration: 'SELF_DECLARATION',
    overseas_agreement: 'OVERSEAS_AGREEMENT',
  };

  const AGREEMENT_FILENAMES = {
    nda: 'NDA_Agreement.pdf',
    sqa: 'SA_Agreement.pdf',
    four_m: '4M_Declaration.pdf',
    code_of_conduct: 'Code_of_Conduct.pdf',
    compliance_agreement: 'Compliance_Agreement.pdf',
    self_declaration: 'Self_Declaration.pdf',
    overseas_agreement: 'Overseas_Agreement.pdf',
  };

  const handleDownloadAgreement = async (agreementType) => {
    try {
      const apiType = AGREEMENT_TYPE_TO_API[agreementType];
      if (!apiType) {
        console.warn('Unknown agreement type:', agreementType);
        return;
      }
      const vendorId = application?.vendor_id || application?.id;
      const pdfBlob = await documentService.getAgreementPdf(vendorId, apiType);
      const url = window.URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = AGREEMENT_FILENAMES[agreementType] || 'agreement.pdf';
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Failed to download agreement PDF:', err);
      toast.error('Failed to download agreement PDF. Please try again.');
    }
  };

  const handlePreviewAgreement = async (agreementType) => {
    try {
      setLoadingAgreement(true);
      const apiType = AGREEMENT_TYPE_TO_API[agreementType];
      if (!apiType) {
        console.warn('Unknown agreement type:', agreementType);
        return;
      }
      const vendorId = application?.vendor_id || application?.id;
      const pdfBlob = await documentService.getAgreementPdf(vendorId, apiType);
      const url = window.URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Failed to load agreement PDF:', err);
      toast.error('Failed to load agreement PDF. Please try again.');
    } finally {
      setLoadingAgreement(false);
    }
  };

  // generateAgreementContent removed; using centralized generators in documentGenerator

  const handleAction = async (action) => {
    if (action === 'reject') {
      setShowRejectionOptions(true);
      return;
    }

    if (action === 'approve') {
      // For LEVEL_2, check if acknowledgement is complete and data exists
      if (application?.approval_level === 'LEVEL_2') {
        const hasAcknowledgement = application.acknowledgement?.status === 'APPROVED' 
                                || application.acknowledgement?.status === 'RESOLVED';
        const hasQuestionnaireData = application.supplier_term_of_payment 
                                    && application.supplier_payment_method;
        
        if (hasAcknowledgement && hasQuestionnaireData) {
          // Skip questionnaire, proceed directly with existing data
          // Use the pre-populated questionnaireData from useEffect
          const existingQuestionnaireData = {
            supplierTermOfPayment: application.supplier_term_of_payment || '',
            supplierPaymentMethod: application.supplier_payment_method || '',
            supplierDeliveryTerms: application.supplier_delivery_terms || '',
            supplierModeOfDelivery: application.supplier_mode_of_delivery || '',
            supplierGroup: application.supplier_group || '',
            supplierCategory: application.supplier_category || '',
            commodityCode: application.commodity_code || ''
          };
          
          // Proceed with approval using existing data
          setIsProcessing(true);
          try {
            const questionnaireString = Object.entries(existingQuestionnaireData)
              .filter(([_, value]) => value)
              .map(([key, value]) => `${key}: ${value}`)
              .join(', ');
            
            await onApprove(application.id, `Level 2 approval - Questionnaire already completed: ${questionnaireString}`, existingQuestionnaireData);
            onClose();
          } catch (error) {
            console.error('Approval failed:', error);
          } finally {
            setIsProcessing(false);
          }
          return;
        }
      }
      
      // Show questionnaire for LEVEL_1 or if data is missing
      setShowQuestionnaire(true);
      return;
    }

    // For request_changes, proceed directly
    setIsProcessing(true);

    try {
      await onRequestChanges(application.id, {});
      onClose();
    } catch (error) {
      console.error('Action failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleQuestionnaireSubmit = async () => {
    // Validate questionnaire
    const questionnaireErrors = validateQuestionnaire();
    if (Object.keys(questionnaireErrors).length > 0) {
      setQuestionnaireErrors(questionnaireErrors);
      toast.warning('Please complete all questionnaire fields');
      return;
    }

    setIsProcessing(true);

    try {
      // Convert questionnaire data to a readable string format
      const questionnaireString = Object.entries(questionnaireData)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');

      await onApprove(application.id, `Questionnaire completed: ${questionnaireString}`, questionnaireData);
      onClose();
    } catch (error) {
      console.error('Approval failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSelectRejectionType = (type) => {
    setRejectionType(type);
    setShowRejectionOptions(false);
    
    if (type === 'permanent') {
      setShowPermanentRejectionConfirmation(true);
    } else if (type === 'rectification') {
      setShowRejectionForm(true);
    }
  };

  const handleConfirmPermanentRejection = async () => {
    setShowPermanentRejectionConfirmation(false);
    setShowRejectionForm(true);
  };

  const handleCancelRejection = () => {
    setShowRejectionOptions(false);
    setShowPermanentRejectionConfirmation(false);
    setShowRejectionForm(false);
    setRejectionType(null);
    setRejectionReason('');
    setCustomReason('');
    setRemarks('');
  };

  const handleReject = async () => {
    // For rectification, remarks are mandatory
    if (rejectionType === 'rectification' && !remarks.trim()) {
      toast.warning('Please provide additional remarks for rectification');
      return;
    }

    // For permanent rejection, reason is required
    if (rejectionType === 'permanent') {
      if (!rejectionReason) {
        toast.warning('Please select a rejection reason');
        return;
      }

      if (rejectionReason === 'other' && !customReason.trim()) {
        toast.warning('Please specify the custom reason');
        return;
      }
    }

    setIsProcessing(true);

    try {
      let finalReason = '';
      if (rejectionType === 'permanent') {
        // Get the proper label for the rejection reason
        if (rejectionReason === 'other') {
          finalReason = customReason;
        } else {
          const reasonOption = rejectionReasons.find(r => r.value === rejectionReason);
          finalReason = reasonOption ? reasonOption.label : rejectionReason;
        }
      }

      // Combine reason with additional remarks
      const fullRejectionMessage = rejectionType === 'permanent' 
        ? (remarks ? `${finalReason} - ${remarks}` : finalReason)
        : remarks;
      
      await onReject(application.id, fullRejectionMessage, rejectionReason === 'other' ? customReason : rejectionReason, remarks, rejectionType);
      onClose();
    } catch (error) {
      console.error('Rejection failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderSupplierInfo = () => (
    <div className="space-y-6">
      {/* Details the vendor typed in because a registry could not answer (or was slow) at registration. */}
      {application.kyc_manual_reason && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="review-manual-entry">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
            <Icon name="ShieldAlert" size={16} /> Entered manually at registration — verify before approving
          </p>
          <ul className="mt-2 space-y-1 text-sm text-amber-900">
            {parseManualReason(application.kyc_manual_reason).map((m, i) => (
              <li key={i}>
                • <strong>{m.what}</strong>{m.why ? `: typed in by the vendor because ${m.why}` : ''}
                {m.at ? ` (${new Date(m.at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })})` : ''}.
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Company Name</label>
          <p className="text-slate-900 font-medium">{application.company_name}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Business Vertical</label>
          <p className="text-slate-900">{formatBusinessVertical(application.business_vertical) || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Category</label>
          <p className="text-slate-900">{formatSupplierCategory(application.supplier_category) || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Supplier Group</label>
          <p className="text-slate-900">{formatSupplierType(formatSupplierGroup(application.supplier_group)) || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Nature of Business</label>
          <p className="text-slate-900" data-testid="review-nature-of-business">{natureLabel(application.nature_of_business)}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Supplier Type (OEM / ODM)</label>
          <p className="text-slate-900">{oemOdmLabel(application.oem_odm)}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Supplier Sub-category</label>
          <p className="text-slate-900">{subcategoryLabel(application.supplier_subcategory)}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Contact Person</label>
          <p className="text-slate-900 font-medium">{application.contact_person_name || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Email</label>
          <p className="text-slate-900">{application.email || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Phone</label>
          <p className="text-slate-900">{application.phone_number || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Country</label>
          <p className="text-slate-900">{getCountryName(application.country_origin) || 'N/A'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Registered Address</label>
          <div className="text-slate-900 whitespace-normal break-words">
            {application.registered_address && (
              <p className="mb-1">{application.registered_address}</p>
            )}
            <p className="text-sm">
              {[application.registered_city, application.registered_state]
                .filter(Boolean)
                .join(', ')}
              {application.registered_pincode && ` - ${application.registered_pincode}`}
              {!application.registered_city && !application.registered_state && !application.registered_pincode && 'N/A'}
            </p>
            {application.registered_country && (
              <p className="text-sm mt-1">{getCountryName(application.registered_country)}</p>
            )}
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Supply Address</label>
          <div className="text-slate-900 whitespace-normal break-words">
            {application.supply_address && (
              <p className="mb-1">{application.supply_address}</p>
            )}
            <p className="text-sm">
              {[application.supply_city, application.supply_state]
                .filter(Boolean)
                .join(', ')}
              {application.supply_pincode && ` - ${application.supply_pincode}`}
              {!application.supply_city && !application.supply_state && !application.supply_pincode && 'N/A'}
            </p>
            {application.supply_country && (
              <p className="text-sm mt-1">{getCountryName(application.supply_country)}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderBankDetails = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Bank Name</label>
          <p className="text-slate-900 font-medium">{application.bank_name || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Account Number</label>
          <p className="text-slate-900">{application.account_number || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Account Type</label>
          <p className="text-slate-900">{formatAccountType(application.account_type) || 'N/A'}</p>
        </div>
        {/*
          An Indian vendor gives an IFSC, everyone else a SWIFT code — both live on the vendor itself.
          This used to read them off `bank_info`, a shape the API has never sent, so a foreign vendor's
          SWIFT code always showed as "N/A" and the reviewer had no way to see it.
        */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">
            {application.country_origin === 'IN' ? 'IFSC Code' : 'SWIFT Code'}
          </label>
          <p className="text-slate-900 font-mono">
            {application.ifsc_code || application.swift_code || 'N/A'}
          </p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Branch</label>
          <p className="text-slate-900">{application.branch_name || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Currency</label>
          <p className="text-slate-900">{application.currency || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Bank Proof</label>
          <div className="flex items-center gap-2">
            <Icon name="FileText" size={16} className="text-blue-600" />
            <span className="text-slate-900">Bank proof document uploaded</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Agreement PDFs (generated by Bull job, stored in S3) - show only in Agreements tab, not Documents tab
  const AGREEMENT_PDF_FILENAMES = [
    'NDA_Agreement.pdf', 'SA_Agreement.pdf', '4M_Declaration.pdf',
    'Code_of_Conduct.pdf', 'Compliance_Agreement.pdf', 'Self_Declaration.pdf', 'Overseas_Agreement.pdf'
  ];
  const uploadedDocuments = (application.documents || []).filter(
    doc => !(doc.document_type === 'OTHER' && AGREEMENT_PDF_FILENAMES.includes(doc.file_name))
  );

  // Compute resubmission status for a document based on document requests
  const getDocumentResubmissionStatus = (doc) => {
    const documentRequests = application.document_requests || [];
    const now = new Date();

    const docMatchesRequest = (req) => {
      const requestedTypes = req.requested_types || [];
      const customDocs = req.custom_docs || [];
      if (doc.document_type !== 'OTHER') {
        return requestedTypes.includes(doc.document_type);
      }
      return customDocs.some((c) => (c.name || '').trim().toLowerCase() === (doc.file_name || '').trim().toLowerCase());
    };

    const matchingRequest = documentRequests.find((req) => docMatchesRequest(req));
    if (!matchingRequest) return null;

    const expiresAt = new Date(matchingRequest.expires_at);
    if (matchingRequest.used) return 'submitted';
    if (expiresAt < now) return 'expired';
    return 'requested';
  };

  const DOCUMENT_TYPE_OPTIONS = [
    { value: 'INCORPORATION_CERTIFICATE', label: 'Company Incorporation' },
    { value: 'GST_CERTIFICATE', label: 'GST Certificate' },
    { value: 'PAN_CARD', label: 'PAN Card' },
    { value: 'BANK_PROOF', label: 'Bank Proof' },
    { value: 'DIGITAL_SIGNATURE', label: 'Digital Signature' },
    ...(application?.msme_status === 'MSME' || application?.msme_status === 'msme'
      ? [{ value: 'MSME_CERTIFICATE', label: 'Udyam Certificate' }]
      : []),
  ];

  const toggleDocType = (value) => {
    setRequestedDocTypes((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]
    );
  };

  const addCustomDoc = () => {
    const name = customDocInput.trim();
    if (name && !customDocs.some((c) => c.name === name)) {
      setCustomDocs((prev) => [...prev, { name }]);
      setCustomDocInput('');
    }
  };

  const removeCustomDoc = (name) => {
    setCustomDocs((prev) => prev.filter((c) => c.name !== name));
  };

  const handleRequestDocuments = async () => {
    if (requestedDocTypes.length === 0 && customDocs.length === 0) {
      toast.error('Please select at least one document type or add a custom document');
      return;
    }
    setIsRequestingDocuments(true);
    try {
      const result = await vendorService.requestDocuments(application.id, {
        requested_types: requestedDocTypes,
        custom_docs: customDocs,
      });
      toast.success('Document request sent. Link emailed to vendor.');
      setShowRequestDocumentsModal(false);
      setRequestedDocTypes([]);
      setCustomDocs([]);
      onRefresh?.();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to send document request');
    } finally {
      setIsRequestingDocuments(false);
    }
  };

  const handleRegenerateMsmeDeclaration = async (docId) => {
    const vendorId = application?.vendor_id || application?.id;
    if (!vendorId) return;
    try {
      setRegeneratingMsmeId(docId);
      await vendorService.regenerateMsmeDeclaration(vendorId);
      toast.success('MSME declaration has been regenerated.');
      onRefresh?.();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to queue regeneration.');
    } finally {
      setRegeneratingMsmeId(null);
    }
  };

  const renderDocuments = () => (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setRequestedDocTypes([]);
            setCustomDocs([]);
            setCustomDocInput('');
            setShowRequestDocumentsModal(true);
          }}
        >
          <Icon name="Send" size={14} className="mr-2" />
          Request Documents
        </Button>
      </div>
      {uploadedDocuments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {uploadedDocuments.map((doc, index) => {
            const resubmissionStatus = getDocumentResubmissionStatus(doc);
            return (
            <div key={index} className="border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="FileText" size={20} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h5 className="font-medium text-card-foreground">{formatDocumentType(doc.document_type)}</h5>
                    {resubmissionStatus && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          resubmissionStatus === 'requested'
                            ? 'bg-amber-100 text-amber-800'
                            : resubmissionStatus === 'submitted'
                            ? 'bg-green-100 text-green-800'
                            : resubmissionStatus === 'expired'
                            ? 'bg-slate-200 text-slate-600'
                            : ''
                        }`}
                      >
                        {resubmissionStatus === 'requested' && 'Resubmission requested'}
                        {resubmissionStatus === 'submitted' && 'Resubmitted'}
                        {resubmissionStatus === 'expired' && 'Request expired'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-text-secondary truncate">{doc.file_name}</p>
                </div>
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-xs text-text-secondary">
                  {doc.expiry_date ? `Expires: ${new Date(doc.expiry_date).toLocaleDateString()}` : 'No expiry'}
                </span>
                <div className="flex items-center space-x-2 flex-wrap">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      try {
                        const response = await documentService.downloadDocument(doc.id);
                        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
                        const url = URL.createObjectURL(blob);
                        window.open(url, '_blank');
                        // Clean up the object URL after a delay
                        setTimeout(() => URL.revokeObjectURL(url), 1000);
                      } catch (e) {
                        console.error(e);
                        toast.error('Preview failed. Please try again.');
                      }
                    }}
                  >
                    <Icon name="Eye" size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      try {
                        const response = await documentService.downloadDocument(doc.id);
                        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = doc.file_name || 'document';
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      } catch (e) {
                        console.error(e);
                        toast.error('Download failed. Please try again.');
                      }
                    }}
                  >
                    <Icon name="Download" size={14} />
                  </Button>
                  {String(doc.document_type).toUpperCase() === 'MSME_DECLARATION' && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={regeneratingMsmeId === doc.id}
                      onClick={() => handleRegenerateMsmeDeclaration(doc.id)}
                    >
                      Regenerate
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
          })}
        </div>
      ) : (
        <div className="text-center py-8">
          <Icon name="FileText" size={48} className="text-text-secondary mx-auto mb-4" />
          <p className="text-text-secondary">No documents uploaded yet</p>
        </div>
      )}
    </div>
  );

  const renderAgreements = () => {
    const status = application.agreement_generation_status;
    const isGenerating = status === 'PENDING' || status === 'GENERATING';

    if (isGenerating) {
      return (
        <div className="space-y-4">
          <div className="flex flex-col items-center justify-center py-12 px-4 border border-border rounded-lg bg-muted/30">
            <Icon name="Loader" size={40} className="animate-spin text-primary mb-4" />
            <p className="font-medium text-foreground">Agreements are being generated</p>
            <p className="text-sm text-text-secondary mt-1 text-center max-w-md">
              This usually takes 30–60 seconds. You can approve the vendor now; agreements will be attached when ready.
            </p>
            {onRefresh && (
              <Button variant="outline" size="sm" className="mt-4" onClick={onRefresh}>
                Refresh
              </Button>
            )}
          </div>
        </div>
      );
    }

    if (status === 'FAILED') {
      const handleRegenerate = async (withSignature) => {
        if (!application?.id || isRegenerating) return;
        setIsRegenerating(true);
        try {
          await vendorService.regenerateAgreements(application.id, withSignature);
          toast.success('Agreement regeneration started. Status will update shortly.');
          onRefresh?.();
        } catch (err) {
          toast.error(getErrorMessage(err) || 'Failed to regenerate agreements');
        } finally {
          setIsRegenerating(false);
        }
      };
      return (
        <div className="flex flex-col items-center justify-center py-12 px-4 border border-destructive/30 rounded-lg bg-destructive/5">
          <Icon name="AlertCircle" size={40} className="text-destructive mb-4" />
          <p className="font-medium text-foreground">Agreement generation failed</p>
          <p className="text-sm text-text-secondary mt-1 mb-4">Regenerate from here instead of re-submitting the form.</p>
          <div className="flex flex-wrap gap-2 justify-center">
              <Button
                variant="default"
                size="sm"
                onClick={() => handleRegenerate(true)}
                disabled={isRegenerating}
              >
                {isRegenerating ? (
                  <>
                    <Icon name="Loader" size={14} className="animate-spin mr-2" />
                    Regenerating…
                  </>
                ) : (
                  'Regenerate with signature'
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRegenerate(false)}
                disabled={isRegenerating}
              >
                Regenerate without signature
              </Button>
            </div>
        </div>
      );
    }

    const handleRegenerate = async (withSignature) => {
      if (!application?.id || isRegenerating) return;
      setIsRegenerating(true);
      try {
        await vendorService.regenerateAgreements(application.id, withSignature);
        toast.success('Agreement regeneration started. Status will update shortly.');
        onRefresh?.();
      } catch (err) {
        toast.error(getErrorMessage(err) || 'Failed to regenerate agreements');
      } finally {
        setIsRegenerating(false);
      }
    };

    const items = [
      { id: 'nda', label: 'Mutual Non-Disclosure Agreement (NDA)', signed: application.nda },
      { id: 'sqa', label: 'Supplier Agreement', signed: application.sqa },
      { id: 'four_m', label: '4M Agreement', signed: application.four_m },
      { id: 'code_of_conduct', label: 'Code of Conduct for Business Partners', signed: application.code_of_conduct },
      { id: 'compliance_agreement', label: 'Compliance Agreement', signed: application.compliance_agreement },
      { id: 'self_declaration', label: 'Self Declaration', signed: application.self_declaration },
      { id: 'overseas_agreement', label: 'Overseas Agreement', signed: application.overseas_agreement }
    ].filter(a => !!a.signed);

    return (
    <div className="space-y-6">
      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 p-4 border border-border rounded-lg bg-muted/30">
          <span className="text-sm font-medium text-foreground">Regenerate agreements:</span>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleRegenerate(true)}
              disabled={isRegenerating}
            >
              {isRegenerating ? (
                <>
                  <Icon name="Loader" size={14} className="animate-spin mr-2" />
                  Regenerating…
                </>
              ) : (
                'Regenerate with signature'
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleRegenerate(false)}
              disabled={isRegenerating}
            >
              Regenerate without signature
            </Button>
          </div>
        </div>
      )}
      <div className="space-y-4">
        {items.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="FileX" size={48} className="text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary">No signed agreements</p>
            <p className="text-xs text-text-secondary mt-2">Documents will be generated automatically during approval</p>
          </div>
        ) : (
          items.map((ag) => (
            <div key={ag.id} className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div className="flex items-center space-x-3">
                <Checkbox checked disabled className="pointer-events-none" />
                <div>
                  <h5 className="font-medium text-card-foreground">{ag.label}</h5>
                  <p className="text-sm text-text-secondary">Signed</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">Signed</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePreviewAgreement(ag.id)}
                  disabled={loadingAgreement}
                >
                  {loadingAgreement ? (
                    <Icon name="Loader" size={14} className="animate-spin" />
                  ) : (
                    <Icon name="Eye" size={14} />
                  )}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDownloadAgreement(ag.id)}>
                  <Icon name="Download" size={14} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Information about automatic document generation */}
      <div className="mt-6 p-4 bg-info/10 border border-info/20 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-info mt-0.5" />
          <div>
            <h4 className="font-medium text-info">Automatic Document Generation</h4>
            <p className="text-sm text-text-secondary mt-1">
              When you approve this vendor, all required PDF documents (NDA, SA Agreement, 4M Declaration, etc.)
              will be automatically generated and attached to the approval email along with the Excel file.
            </p>
          </div>
        </div>
      </div>
    </div>
    );
  };

  const renderQuestionnaire = () => {
    // Check if there's questionnaire data in the application history
    const questionnaireEntry = application.history?.find(event =>
      event.comments && event.comments.includes('Questionnaire completed:')
    );

    if (!questionnaireEntry) {
      return (
        <div className="text-center py-8">
          <Icon name="ClipboardList" size={48} className="text-text-secondary mx-auto mb-4" />
          <p className="text-text-secondary">No questionnaire data available</p>
          <p className="text-xs text-text-secondary mt-2">Questionnaire will be filled during approval process</p>
        </div>
      );
    }

    // Extract questionnaire data from comments
    const questionnaireString = questionnaireEntry.comments.replace('Questionnaire completed: ', '');
    const questionnairePairs = questionnaireString.split(', ');

    // Parse the questionnaire data
    const questionnaireData = {};
    questionnairePairs.forEach(pair => {
      const [key, value] = pair.split(': ');
      if (key && value) {
        questionnaireData[key] = value;
      }
    });

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Questionnaire Responses</h3>
          <div className="flex items-center space-x-2">
            <Icon name="Calendar" size={16} className="text-text-secondary" />
            <span className="text-sm text-text-secondary">
              Completed on {new Date(questionnaireEntry.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Payment Terms */}
          <div className="space-y-3">
            <h4 className="font-medium text-card-foreground">Payment Information</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-text-secondary">Terms of Payment</label>
                <p className="text-card-foreground font-medium">
                  {questionnaireData.supplierTermOfPayment || 'Not specified'}
                </p>
              </div>
              <div>
                <label className="text-sm text-text-secondary">Payment Method</label>
                <p className="text-card-foreground font-medium">
                  {questionnaireData.supplierPaymentMethod || 'Not specified'}
                </p>
              </div>
            </div>
          </div>

          {/* Delivery Information */}
          <div className="space-y-3">
            <h4 className="font-medium text-card-foreground">Delivery Information</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-text-secondary">Delivery Terms</label>
                <p className="text-card-foreground font-medium">
                  {questionnaireData.supplierDeliveryTerms || 'Not specified'}
                </p>
              </div>
              <div>
                <label className="text-sm text-text-secondary">Mode of Delivery</label>
                <p className="text-card-foreground font-medium">
                  {questionnaireData.supplierModeOfDelivery || 'Not specified'}
                </p>
              </div>
            </div>
          </div>

          {/* Supplier Classification */}
          <div className="space-y-3">
            <h4 className="font-medium text-card-foreground">Supplier Classification</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-text-secondary">Supplier Group</label>
                <p className="text-card-foreground font-medium">
                  {questionnaireData.supplierGroup || 'Not specified'}
                </p>
              </div>
              <div>
                <label className="text-sm text-text-secondary">Commodity Code</label>
                <p className="text-card-foreground font-medium">
                  {questionnaireData.commodityCode || 'Not specified'}
                </p>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-3">
            <h4 className="font-medium text-card-foreground">Additional Details</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-text-secondary">Approved By</label>
                <p className="text-card-foreground font-medium">
                  {questionnaireEntry.approved_by || 'System'}
                </p>
              </div>
              <div>
                <label className="text-sm text-text-secondary">Approval Level</label>
                <p className="text-card-foreground font-medium">
                  {questionnaireEntry.level === 'level_1' ? 'Level 1' :
                    questionnaireEntry.level === 'level_2' ? 'Level 2' :
                      questionnaireEntry.level === 'final' ? 'Final' :
                        questionnaireEntry.level?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Level 1'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Raw Data View (for debugging) */}
        <div className="mt-6 p-4 bg-muted/30 rounded-lg">
          <h4 className="font-medium text-card-foreground mb-3">Raw Questionnaire Data</h4>
          <pre className="text-xs text-text-secondary whitespace-pre-wrap">
            {JSON.stringify(questionnaireData, null, 2)}
          </pre>
        </div>
      </div>
    );
  };

  const renderHistory = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground">Application History</h3>
      {application.history && application.history.length > 0 ? (
        <div className="space-y-3">
          {application.history.map((event, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 bg-muted/30 rounded-lg">
              <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">
                  {event.status === 'approved' ? 'Approved' :
                    event.status === 'rejected' ? 'Rejected' :
                      event.status === 'pending' ? 'Under Review' : 'Status Updated'}
                </p>
                <p className="text-xs text-text-secondary">
                  {new Date(event.created_at).toLocaleString()}
                </p>
                {event.comments && (
                  <p className="text-sm text-text-secondary mt-1">{event.comments}</p>
                )}
                <p className="text-xs text-text-secondary mt-1">
                  Level: {event.level === 'level_1' ? 'Level 1' :
                    event.level === 'level_2' ? 'Level 2' :
                      event.level === 'final' ? 'Final' :
                        event.level.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <Icon name="Clock" size={48} className="text-text-secondary mx-auto mb-4" />
          <p className="text-text-secondary">No approval history available</p>
        </div>
      )}
    </div>
  );



  const renderTabContent = () => {
    switch (activeTab) {
      case 'supplier': return renderSupplierInfo();
      case 'bank': return renderBankDetails();
      case 'documents': return renderDocuments();
      case 'agreements': return renderAgreements();
      case 'questionnaire': return renderQuestionnaire();
      case 'history': return renderHistory();
      default: return renderSupplierInfo();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl border border-slate-200 max-w-7xl w-full max-h-[calc(95vh/var(--app-scale))] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{application.company_name}</h2>
            <p className="text-sm text-slate-600 mt-1">
              {isQualityStep
                ? 'Quality approval • On hold until the quality department clears this supplier'
                : `Application Review • ${application.status.replace('_', ' ')}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-full p-2 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 bg-white flex-shrink-0">
          <nav className="flex px-6 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-4 border-b-2 font-semibold text-sm whitespace-nowrap transition-colors ${activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
              >
                <Icon name={tab.icon} size={18} />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {renderTabContent()}
        </div>

        {/* Actions */}
        <div className="bg-slate-50 px-6 py-4 flex justify-end gap-3 border-t border-slate-200 flex-shrink-0">
          {(() => {
            const isRejected = application?.status === 'REJECTED' || application?.status === 'rejected';

            // Check if vendor already has FINAL approval (from history or approvals array)
            // This handles stale LEVEL_2 PENDING records where vendor was already FINAL approved
            const hasFinalApproved = 
              (application.history && Array.isArray(application.history) && 
                application.history.some(event => 
                  (event.level === 'FINAL' || event.level === 'final') && 
                  (event.status === 'APPROVED' || event.status === 'approved')
                )) ||
              (application.approvals && Array.isArray(application.approvals) && 
                application.approvals.some(approval => 
                  (approval.level === 'FINAL' || approval.level === 'final') && 
                  (approval.status === 'APPROVED' || approval.status === 'approved')
                ));

            // Universal check: If vendor has acknowledged (APPROVED/RESOLVED), Level 1 is complete
            // This works for both LEVEL_1 and LEVEL_2 approvals
            const hasVendorAcknowledged = 
              application.acknowledgement?.status === 'APPROVED' || 
              application.acknowledgement?.status === 'RESOLVED';

            // Check if waiting for vendor acknowledgement (works for any approval level)
            const isAwaitingAcknowledgement = 
              application.acknowledgement?.status === 'PENDING';

            // Debug logging
            console.log('ReviewPanel Debug:', {
              approval_status: application?.approval_status,
              approval_level: application?.approval_level,
              acknowledgement_status: application.acknowledgement?.status,
              hasFinalApproved,
              hasVendorAcknowledged,
              isAwaitingAcknowledgement,
              isRejected,
              status: application?.status,
              historyLength: application.history?.length,
              approvalsLength: application.approvals?.length
            });

            if (isRejected) {
              return (
                <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-center gap-2">
                  <Icon name="XCircle" size={16} />
                  <span>This vendor has been rejected and cannot be approved or rejected again.</span>
                </div>
              );
            }

            // The quality gate (RM / PM suppliers, before Level 1): cleared or rejected here, by an
            // administrator or one of the assigned quality approvers.
            if (isQualityStep) {
              if (qualityDecision?.canDecide) {
                return (
                  <>
                    <Button
                      variant="destructive"
                      onClick={() => { setQualityReason(''); setShowQualityReject(true); }}
                      disabled={qualityBusy}
                      iconName="XCircle"
                      iconPosition="left"
                    >
                      Reject
                    </Button>
                    <Button
                      variant="success"
                      onClick={() => decideQuality('APPROVED')}
                      disabled={qualityBusy}
                      loading={qualityBusy}
                      iconName="CheckCircle"
                      iconPosition="left"
                    >
                      Quality approved
                    </Button>
                  </>
                );
              }
              return (
                <div className="px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center gap-2" data-testid="quality-read-only">
                  <Icon name="Lock" size={16} />
                  <span>Waiting for {qualityDecision?.approverNames || 'the quality department'} to clear this supplier. You have view-only access.</span>
                </div>
              );
            }

            if (hasFinalApproved) {
              // Find the most recent FINAL approval date
              const finalApproval = 
                (application.history && Array.isArray(application.history) && 
                  application.history.find(event => 
                    (event.level === 'FINAL' || event.level === 'final') && 
                    (event.status === 'APPROVED' || event.status === 'approved')
                  )) ||
                (application.approvals && Array.isArray(application.approvals) && 
                  application.approvals.find(approval => 
                    (approval.level === 'FINAL' || approval.level === 'final') && 
                    (approval.status === 'APPROVED' || approval.status === 'approved')
                  ));
              
              const finalApprovalDate = finalApproval?.created_at 
                ? new Date(finalApproval.created_at).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                : 'previously';

              return (
                <div className="px-4 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800 flex items-center gap-2">
                  <Icon name="CheckCircle" size={16} />
                  <span>This vendor is already FINAL approved on {finalApprovalDate}. No further actions available.</span>
                </div>
              );
            }

            const hasConflict = application.acknowledgement?.status === 'CONFLICTED';

if (hasConflict) {
  return (
    <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-center gap-2">
      <Icon name="AlertTriangle" size={16} />
      <span>
        This vendor has proposed changes to the questionnaire. 
        Please resolve the conflict in the <Link to="/conflicting-vendors" className="underline font-medium">Conflicting Vendors</Link> page before proceeding.
      </span>
    </div>
  );
}

            if (isAwaitingAcknowledgement) {
              return (
                <div className="px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center gap-2">
                  <Icon name="Clock" size={16} />
                  <span>Waiting for vendor acknowledgement. Approval actions will be available after vendor responds.</span>
                </div>
              );
            }

            // Only show "in Level 2 workflow" message when viewing from Level 1 context (approval_level is LEVEL_1).
            // When approval_level is LEVEL_2, we're in the Assign Vendor Code tab — show Assign Vendor Code.
            if (hasVendorAcknowledged && application?.approval_level === 'LEVEL_1') {
              return (
                <div className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 flex items-center gap-2">
                  <Icon name="Info" size={16} />
                  <span>Vendor has acknowledged. Level 1 approval is complete. This vendor is now in the Assign Vendor Code workflow.</span>
                </div>
              );
            }

            // Level 2 (Assign Vendor Code tab): show Assign Vendor Code instead of Approve/Reject
            if (application?.approval_level === 'LEVEL_2' && isAdmin && onAssignVendorCode) {
              const hasVendorCode = !!application.vendor_code;
              
              if (hasVendorCode) {
                return (
                  <div className="px-4 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800 flex items-center gap-2">
                    <Icon name="CheckCircle" size={16} />
                    <span>Vendor code already assigned/synced: <strong>{application.vendor_code}</strong></span>
                  </div>
                );
              }

              return (
                <Button
                  variant="default"
                  onClick={() => setShowAssignVendorCodeModal(true)}
                  iconName="Hash"
                  iconPosition="left"
                >
                  Assign Vendor Code
                </Button>
              );
            }

            if (isAdmin) {
              return (
                <>
                  <Button
                    variant="destructive"
                    onClick={() => handleAction('reject')}
                    disabled={isProcessing}
                    iconName="XCircle"
                    iconPosition="left"
                  >
                    Reject
                  </Button>
                  <Button
                    variant="success"
                    onClick={() => handleAction('approve')}
                    disabled={isProcessing}
                    loading={isProcessing}
                    iconName="CheckCircle"
                    iconPosition="left"
                  >
                    Approve
                  </Button>
                </>
              );
            }

            return (
              <div className="px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center gap-2">
                <Icon name="Lock" size={16} />
                <span>
                  {application?.approval_level === 'LEVEL_2'
                    ? 'You have view-only access. Only admins can assign vendor code.'
                    : 'You have view-only access. Only admins can approve or reject applications.'}
                </span>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Quality rejection: a reason is required */}
      {showQualityReject && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[101] flex items-center justify-center p-4" data-testid="quality-reject-dialog">
          <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-xl font-bold text-slate-900">Reject at quality</h3>
              <p className="text-sm text-slate-600 mt-1">
                {application.company_name} will be marked as rejected and will not go to Level 1 approval.
              </p>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={4}
                value={qualityReason}
                onChange={(e) => setQualityReason(e.target.value)}
                placeholder="What did quality find?"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3">
              <Button variant="outline" onClick={() => setShowQualityReject(false)} disabled={qualityBusy}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => decideQuality('REJECTED')}
                disabled={!qualityReason.trim() || qualityBusy}
                loading={qualityBusy}
                iconName="XCircle"
                iconPosition="left"
              >
                Confirm rejection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Request Documents Modal */}
      {showRequestDocumentsModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[101] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-md relative max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-slate-900">Request Documents</h3>
              <p className="text-sm text-slate-600 mt-1">Select documents to request from the vendor. A link will be emailed to them.</p>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Document types</label>
                <div className="space-y-2">
                  {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={requestedDocTypes.includes(opt.value)}
                        onCheckedChange={() => toggleDocType(opt.value)}
                      />
                      <span className="text-sm text-slate-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Custom document</label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="e.g. Trade License"
                    value={customDocInput}
                    onChange={(e) => setCustomDocInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomDoc())}
                  />
                  <Button variant="outline" size="sm" onClick={addCustomDoc}>
                    Add
                  </Button>
                </div>
                {customDocs.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {customDocs.map((c) => (
                      <li key={c.name} className="flex items-center justify-between text-sm bg-slate-50 px-2 py-1 rounded">
                        <span>{c.name}</span>
                        <button
                          type="button"
                          onClick={() => removeCustomDoc(c.name)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Icon name="X" size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3 flex-shrink-0">
              <Button variant="outline" onClick={() => setShowRequestDocumentsModal(false)}>
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleRequestDocuments}
                disabled={isRequestingDocuments || (requestedDocTypes.length === 0 && customDocs.length === 0)}
                loading={isRequestingDocuments}
              >
                Send Request
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Vendor Code Modal (Level 2) */}
      {showAssignVendorCodeModal && onAssignVendorCode && (
        <AssignVendorCodeModal
          vendor={application}
          isOpen={showAssignVendorCodeModal}
          onClose={() => setShowAssignVendorCodeModal(false)}
          onSubmit={async (vendorCode) => {
            await onAssignVendorCode(vendorCode);
            setShowAssignVendorCodeModal(false);
          }}
        />
      )}

      {/* Rejection Type Selection Modal */}
      {showRejectionOptions && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[101] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-md relative">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-xl font-bold text-slate-900">Select Rejection Action</h3>
              <p className="text-sm text-slate-600 mt-1">Choose how you want to reject this vendor</p>
            </div>
            
            <div className="p-6 space-y-4">
              <Button
                variant="destructive"
                onClick={() => handleSelectRejectionType('permanent')}
                className="w-full"
                iconName="XCircle"
                iconPosition="left"
              >
                Reject Vendor (Permanent)
              </Button>
              <Button
                variant="outline"
                onClick={() => handleSelectRejectionType('rectification')}
                className="w-full"
                iconName="Edit"
                iconPosition="left"
              >
                Reject with Correction
              </Button>
            </div>
            
            <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={handleCancelRejection}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent Rejection Confirmation Dialog */}
      {showPermanentRejectionConfirmation && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[102] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border-2 border-red-500 shadow-xl w-full max-w-md relative">
            <div className="p-6 border-b border-red-200 bg-red-50">
              <h3 className="text-xl font-bold text-red-900">⚠️ Warning</h3>
            </div>
            
            <div className="p-6">
              <p className="text-lg font-semibold text-red-900 text-center mb-4">
                YOU HAVE CHOOSED TO REJECT THE VENDOR. ALL DETAILS OF THIS VENDOR WILL BE DELETED PERMANENTLY. WISH TO PROCEED?
              </p>
            </div>
            
            <div className="p-6 border-t border-red-200 flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={handleCancelRejection}
              >
                NO / CANCEL
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmPermanentRejection}
              >
                YES
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Form Modal */}
      {showRejectionForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[101] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-md relative">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-xl font-bold text-slate-900">
                {rejectionType === 'rectification' ? 'Reject with Correction' : 'Reject Application'}
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                {rejectionType === 'rectification' 
                  ? 'Please provide remarks about what needs to be corrected'
                  : 'Please provide a reason for rejection'}
              </p>
            </div>

            <div className="p-6 space-y-4">
              {rejectionType === 'permanent' && (
                <>
                  <Select
                    label="Rejection Reason *"
                    placeholder="Select reason for rejection"
                    options={rejectionReasons}
                    value={rejectionReason}
                    onChange={setRejectionReason}
                  />
                  
                  {rejectionReason === 'other' && (
                    <Input
                      label="Custom Reason *"
                      type="text"
                      placeholder="Specify the reason..."
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                    />
                  )}
                </>
              )}

              <Input
                label={rejectionType === 'rectification' ? 'Additional Remarks *' : 'Additional Remarks'}
                type="textarea"
                placeholder={rejectionType === 'rectification' ? 'Specify what needs to be corrected...' : 'Any additional comments...'}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>

            <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={handleCancelRejection}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={isProcessing}
                loading={isProcessing}
              >
                Confirm Rejection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Questionnaire Modal */}
      {showQuestionnaire && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[101] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-[98vw] max-h-[calc(98vh/var(--app-scale))] h-[calc(95vh/var(--app-scale))] flex flex-col relative">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">
                {isIndianVendor ? 'Indian Vendor' : 'Foreign Vendor'} Questionnaire
              </h3>
              <Button variant="ghost" size="icon" onClick={() => setShowQuestionnaire(false)}>
                <Icon name="X" size={20} />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-8">
              {isIndianVendor ? (
                <IndianVendorQuestionnaire
                  formData={questionnaireData}
                  updateFormData={updateQuestionnaireData}
                  errors={questionnaireErrors}
                />
              ) : (
                <ForeignVendorQuestionnaire
                  formData={questionnaireData}
                  updateFormData={updateQuestionnaireData}
                  errors={questionnaireErrors}
                />
              )}
            </div>

            <div className="p-6 border-t border-border flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowQuestionnaire(false)}
              >
                Cancel
              </Button>
              <Button
                variant="success"
                onClick={handleQuestionnaireSubmit}
                disabled={isProcessing}
                loading={isProcessing}
              >
                Confirm Approval
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewPanel;